import React from 'react';
import Button from 'react-bootstrap/lib/Button';
import reduxForm, { InjectedFormProps } from 'redux-form/lib/reduxForm';
import Field from 'redux-form/lib/Field';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import styled from '@emotion/styled';
import FieldFormGroup from '../../../../components/FieldFormControl';
import Spinner from '../../../../components/Spinner';
import serverRenderingUtils from '../../../../../helpers/serverRenderingUtils';
import StyledSelectDropdown from '../../../../components/StyledSelectDropdown';
import { Container, FormItem, buttonStyles } from './styles';
import Box from '../../../../shared/Box';
import Text from '../../../../shared/Text';
import { FieldInputWithRedux } from '../../../../components/FieldInput';
import { Metadata } from '../../../../types/Metadata';

const maxLength100 = serverRenderingUtils.maxLength(100);
const maxLength600 = serverRenderingUtils.maxLength(600);

const validate = (values: Metadata) => {
  const allFieldsFilledIn =
    values.podcastName &&
    values.podcastDescription &&
    values.itunesCategory &&
    values.language;
  return allFieldsFilledIn ? {} : { _error: 'Not everything is filled in..' };
};
// todo: put this somewhere else
const LANGUAGE_MAP: { [language: string]: string } = {};

serverRenderingUtils.getLanguagesSortedByLabel(true).forEach((lang: Option) => {
  LANGUAGE_MAP[lang.value] = lang.label;
});

const Capitalized = styled.p`
  &:first-letter {
    text-transform: uppercase;
  }
`;
type Option = { label: string; value: string };
type CustomProps = {
  podcastCategoryOptions: Option[];
  stationType: string;
  isSubmitting: boolean;
};

type MetadataFormProps = CustomProps & InjectedFormProps<any, CustomProps>;

const MetadataForm = ({
  invalid,
  isSubmitting,
  handleSubmit,
  podcastCategoryOptions,
  stationType,
}: MetadataFormProps) => (
  <Container>
    <form
      onSubmit={e => {
        // this will prevent any other forms on the page from unintentionally submitted
        e.stopPropagation();
        e.preventDefault();
        // @ts-ignore
        handleSubmit();
      }}
    >
      <FormItem aria-label="podcast name">
        <Capitalized>{stationType} name</Capitalized>
        <Field
          name="podcastName"
          component={FieldInputWithRedux}
          type="text"
          maxLength={100}
          normalize={maxLength100}
        />
      </FormItem>
      <FormItem aria-label="podcast description">
        <Capitalized>{stationType} description</Capitalized>
        <Field
          name="podcastDescription"
          component={FieldFormGroup}
          componentClass="textarea"
          rows={4}
          type="text"
          maxLength={600}
          normalize={maxLength600}
        />
      </FormItem>
      <FormItem aria-label="podcast category">
        <Capitalized>{stationType} category</Capitalized>
        <Field
          name="itunesCategory"
          component={StyledSelectDropdown}
          id="category-dropdown"
          defaultValue="Choose one..."
          renderTitle={(categoryValue: string) =>
            categoryValue ? categoryValue.split('|').pop() : 'Choose one...'
          }
        >
          {podcastCategoryOptions.map(category => (
            <MenuItem key={`${category.label}`} eventKey={category.value}>
              <Text size="lg" color="#292f36">
                {category.label}
              </Text>
            </MenuItem>
          ))}
        </Field>
      </FormItem>
      <FormItem aria-label="podcast language">
        <p>Language</p>
        <Field
          name="language"
          component={StyledSelectDropdown}
          id="language-dropdown"
          defaultValue="Choose one..."
          renderTitle={(lang: string | number) =>
            LANGUAGE_MAP[lang] || 'Choose one...'
          }
        >
          <Box marginBottom={11}>
            <Box marginTop={14} marginBottom={14}>
              <Text size="lg" color="#292f36" align="center">
                Please pick the closest selection to your language.
              </Text>
            </Box>
            <Box height={1} width="100%" color="#dddddd" />
          </Box>
          {serverRenderingUtils
            .getLanguagesSortedByLabel(true)
            .map((language: Option) => (
              <MenuItem key={language.label} eventKey={language.value}>
                <Text size="lg" color="#292f36">
                  {language.label}
                </Text>
              </MenuItem>
            ))}
        </Field>
      </FormItem>
      <Button
        className={buttonStyles}
        block
        bsStyle="primary"
        type="submit"
        disabled={isSubmitting || invalid}
      >
        {isSubmitting ? <Spinner size={24} /> : 'Continue'}
      </Button>
    </form>
  </Container>
);

export default reduxForm<any, CustomProps>({
  form: 'PreDistributionModalScreen.MetadataForm',
  enableReinitialize: true,
  validate,
})(MetadataForm);
